/* Copyright (c) 2020-present CloudIO Inc */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  useEvent,
  useStateX,
  useStateXGetter,
  useStateXSelector,
  useStateXSetter,
} from '@cloudio-saas/statex';
import clsx from 'clsx';
import { useRowIndex, useStaticRuntime } from 'common/Context';
import {
  AnyItem,
  ComponentItemType,
  ComponentItemTypeRegistry,
  ComponentManager,
  getManager,
  Item,
  ItemID,
  ItemType,
  Page,
  Platform,
  registerComponents,
  RequiredItem,
  useMetadataValue,
} from 'componentregistry';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { designPath } from 'designer/atoms';
import {
  emptyVisibleChildrenSelector,
  genId,
  visibleChildItemsSelector,
  visibleChildrenSelector,
} from 'designer/state';
import useDesigner from 'designer/useDesigner';
import { motion } from 'framer-motion';
import {
  getDataStoreAlias,
  runtimeContextMenuAtom,
} from 'layout/Sidebar/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, SetStateAction, useMemo } from 'react';
import { useIsDeveloper, useIsViewOnlyUser } from 'root/useUser';
import { getDomProps } from 'utils/cache';
import { useUpdateOnHMR } from 'utils/hmr';
import { NOT_DEFINED } from 'utils/isEmpty';
import OverlayPortal from 'utils/OverlayPortal';
import { useComponentTracker } from '../hooks/useComponentTracker';
import { hasPropTillRoot } from '../utils/clickOnEnterOrSpace';

const wrapComponent = <K extends keyof ComponentItemTypeRegistry>(
  itemType: K,
  ActualComponent: React.FunctionComponent<any>,
  custom = false,
): [React.FunctionComponent<any>, React.FunctionComponent<any>] => {
  const Component = (props: any, ref: React.Ref<HTMLElement>) => {
    const { appUid, pageId, itemId } = props;
    const metaProps = useMetadataValue<K>(appUid, pageId, itemId, itemType);
    const { visible, visibleCondition, comments, responsive, ...otherProps } =
      metaProps;
    const childrenItems = useChildrenInternal({
      appUid,
      pageId,
      itemId,
      itemType,
    });

    // useEffect(() => {
    //   console.log(`Mouting ${itemType} ${itemId}`);
    //   return () => {
    //     console.log(`Unmounting ${itemType} ${itemId}`);
    //   };
    // }, [itemId]);

    useComponentTracker(itemType);
    // console.log(itemType, metaProps, props);
    // const p: Record<string, unknown> = { ...props, ...metaProps };
    // delete p.platform;
    // useWhyDidYouUpdate(`${itemType}.${itemId}`, { ...props }, true);

    // remove empty style object from the props
    const { style, ...noneStyleProps } = props;
    const p =
      !style || Object.keys(style).length === 0 ? noneStyleProps : props;

    const className = clsx(props.className, otherProps.className);
    return (
      // @ts-ignore dynamic item
      <ActualComponent ref={ref} {...otherProps} {...p} className={className}>
        {childrenItems.length ? childrenItems : undefined}
      </ActualComponent>
    );
  };
  Component.displayName = itemType;

  const MemoComponent = memo(React.forwardRef(Component));

  const _mgr = getManager(itemType) as ComponentManager<K>;

  let DEVComponent: React.ForwardRefRenderFunction<any>;
  if (custom) {
    const MotionComponent = _mgr.noOverlay
      ? MemoComponent
      : motion(MemoComponent);
    MotionComponent.displayName = `Motion${itemType}`;
    DEVComponent = (props: any, r: React.Ref<HTMLElement>) => {
      const { appUid, pageId, itemId, platform } = props;
      const { ref, motionProps, devProps, Overlay, mgr } = useDesigner(
        appUid,
        pageId,
        itemId,
        platform,
      );
      ref.join(r);
      const devClick = devProps.onClick;
      const onClick = useEvent((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        devClick?.(e);
        if (mgr.allowClick) {
          ref.current?.click();
        }
      });
      // useWhyDidYouUpdate(
      //   'DEVComponent',
      //   { ...props, ...motionProps, ...devProps },
      //   true,
      // );

      return (
        <>
          {/* @ts-ignore TODO */}
          <MotionComponent
            ref={ref}
            {...(mgr.noOverlay ? {} : motionProps)}
            {...props}
            {...devProps}
          />
          {!mgr.noOverlay &&
          Overlay &&
          (!mgr.canHaveChildren() ||
            mgr.appendOverlay ||
            mgr.prependOverlay) ? (
            <OverlayPortal
              key="overlayPortal"
              parentRef={ref}
              prepend={
                mgr.prependOverlay ||
                (mgr.canHaveChildren() && !mgr.appendOverlay)
              }
              itemId={itemId}
              itemType={itemType}
              overlayZIndex={mgr.overlayZIndex}
            >
              <Overlay {...devProps} onClick={onClick} aria-label={mgr.name} />
            </OverlayPortal>
          ) : null}
        </>
      );
    };
  } else {
    DEVComponent = (props: any, r: React.Ref<HTMLElement>) => {
      const { appUid, pageId, itemId, platform } = props;
      const { ref, motionProps, devProps, Overlay, mgr } = useDesigner(
        appUid,
        pageId,
        itemId,
        platform,
      );
      ref.join(r);
      let p = props;
      let mp = motionProps;
      if (mgr.noOverlay) {
        p = { ...p, ...devProps };
      } else if (mgr.canHaveChildren()) {
        // for containers we don't create overlay and hence add the devProps to the motion.div
        mp = { ...motionProps, ...devProps };
      }

      return (
        <motion.div ref={ref} {...mp}>
          {/* @ts-ignore TODO */}
          <MemoComponent {...p} />
          {Overlay === null || mgr.canHaveChildren() || mgr.noOverlay ? null : (
            <Overlay {...devProps} />
          )}
        </motion.div>
      );
    };
  }

  DEVComponent.displayName = `DEV${itemType}`;
  return [MemoComponent, React.forwardRef(DEVComponent)];
};

const registerComponent = <K extends keyof ComponentItemTypeRegistry>(
  itemType: K,
  ActualComponent: React.FunctionComponent<any>,
  custom = false,
): void => {
  const [Component, DEVComponent] = wrapComponent(
    itemType,
    ActualComponent,
    custom,
  );
  registerComponents(itemType, Component, DEVComponent);
};

function useData<T>(
  pageId: ItemID,
  itemId: string,
  defaultValue: T,
  type: 'design' | 'runtime',
) {
  const [props, setAllProps] = useStateX<T>(
    ['pageStore', type, pageId, itemId],
    defaultValue,
  );
  const set = useEvent(<K extends keyof T>(key: K, value: T[K]) => {
    setAllProps((p) => ({ ...p, [key]: value }));
  });
  const setProps = useEvent((propsOrFn: SetStateAction<Partial<T>>) => {
    setAllProps((p) => {
      const _props = typeof propsOrFn === 'function' ? propsOrFn(p) : propsOrFn;
      return { ...p, ..._props };
    });
  });
  return { props, set, setProps };
}

function useVisibleChildItems(
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
): AnyItem[] {
  const platform = usePlatform();
  const rowIndex = useRowIndex();
  const store = useCurrentDataStore();

  return useStateXSelector(visibleChildItemsSelector, {
    params: { appUid, pageId, itemId, rowIndex: `r-${rowIndex}-childItems` },
    props: { platform, rowIndex, dsAlias: store?.alias ?? NOT_DEFINED },
  });
}

const getWrapperPropsCache = new Map<string, Record<string, unknown>>();

function useChildren<K extends keyof ComponentItemTypeRegistry>(props: {
  itemType: K;
  itemId: ItemID;
  appUid: string;
  pageId: ItemID;
  excludeChildren?: boolean;
}): JSX.Element[] {
  const { appUid, pageId, itemId, excludeChildren } = props;
  const platform = usePlatform();
  const get = useStateXGetter();
  const set = useStateXSetter();
  const runtime = useStaticRuntime();
  // const mirrored = useIsMirrored();
  const devMode = platform.isDeveloperMode(pageId) && !runtime;
  const hmr = useUpdateOnHMR();
  const rowIndex = useRowIndex();
  const store = useCurrentDataStore();
  // avoid calling visibleChildrenSelector if excludeChildren is true
  const { children, childrenTypes } = useStateXSelector(
    excludeChildren ? emptyVisibleChildrenSelector : visibleChildrenSelector,
    {
      params: { appUid, pageId, itemId, rowIndex: `r-${rowIndex}` },
      props: { platform, rowIndex, dsAlias: store?.alias ?? NOT_DEFINED },
    },
  );
  const developer = useIsDeveloper(appUid);
  const isViewOnlyUser = useIsViewOnlyUser(appUid);

  // useWhyDidYouUpdate(
  //   'useChildren',
  //   { ...props, children, devMode, platform },
  //   true,
  // );

  const elements = useMemo(() => {
    if (
      excludeChildren ||
      children.length === 0 ||
      childrenTypes.length === 0
    ) {
      return EMPTY_ARRAY;
    }

    const page = get<Page>(designPath({ appUid, pageId }));
    const parentItem = page[itemId];
    const parentAlias = getDataStoreAlias(parentItem);
    const list = children
      .map((childID, index) => {
        const item = page[childID];
        if (!item) {
          return <span>Missing item with itemId {childID}</span>;
        }
        const mgr = getManager(item.type);
        if (!mgr) {
          return (
            <span key={childID}>
              Unknown item type {item.type} for used in item with itemId{' '}
              {childID}
            </span>
          );
        }
        if (mgr.managerType !== 'Component') {
          return null;
        }
        const { Component, DEVComponent } = mgr;
        const key = `${childID}-${pageId}-${appUid}`;
        let wrapperProps = getWrapperPropsCache.get(key);
        if (!wrapperProps) {
          wrapperProps = mgr.getWrapperProps({
            item,
            platform,
            pageId,
            appUid,
          }) as Record<string, unknown>;
          if (Object.keys(wrapperProps).length > 0) {
            getWrapperPropsCache.set(key, wrapperProps);
          }
        }
        const dsAlias =
          (getDataStoreAlias(item) || parentAlias || store?.alias) ?? '';
        const domProps =
          !devMode && (developer || isViewOnlyUser) && !runtime
            ? getDomProps(`rt-${childID}`, {
                'data-testid': childID,
                onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
                  const { target } = e;
                  if (
                    target instanceof HTMLInputElement ||
                    target instanceof HTMLTextAreaElement ||
                    hasPropTillRoot(target as HTMLElement, 'contentEditable')
                  ) {
                    return;
                  }
                  e.stopPropagation();
                  e.preventDefault();
                  set(runtimeContextMenuAtom, {
                    pageId,
                    itemId: childID,
                    left: e.clientX,
                    top: e.clientY,
                    orphan: false,
                    dsAlias,
                    rowIndex: rowIndex ?? -1,
                  });
                },
              })
            : getDomProps(childID, {
                // 'data-testid': `${mirrored ? 'm-' : ''}${childID}`,
                'data-testid': childID,
              });
        if (!devMode) {
          if (!Component) {
            throw Error('Missing Component!');
          }
          return (
            <Component
              index={index}
              appUid={appUid}
              pageId={pageId}
              itemId={childID}
              itemType={mgr.type}
              platform={platform}
              {...wrapperProps}
              key={childID}
              domProps={domProps}
            />
          );
        }
        if (!DEVComponent) {
          throw Error('Missing Component!');
        }
        return (
          <DEVComponent
            index={index}
            appUid={appUid}
            pageId={pageId}
            itemId={childID}
            itemType={mgr.type}
            platform={platform}
            {...wrapperProps}
            key={childID}
            domProps={domProps}
            hmr={hmr}
          />
        );
      })
      .filter((x) => x !== null) as JSX.Element[];

    return list.length ? list : EMPTY_ARRAY;
  }, [
    excludeChildren,
    children,
    childrenTypes,
    appUid,
    pageId,
    itemId,
    store?.alias,
    devMode,
    developer,
    isViewOnlyUser,
    runtime,
    platform,
    hmr,
    rowIndex,
  ]);

  return elements;
}

const EMPTY_ARRAY: JSX.Element[] = [];

function useChildrenInternal<K extends ComponentItemType>(props: {
  itemType: K;
  appUid: string;
  itemId: ItemID;
  pageId: string;
}): JSX.Element[] {
  const { itemType } = props;
  const m = getManager(itemType) as unknown as ComponentManager<K>;
  return useChildren({ ...props, excludeChildren: m.excludeChildren });
}

// function useMetadataValueWithNoSubscription<T extends ComponentItemType>(
//   pageId: ItemID,
//   itemId: string,
//   type: T,
// ): Required<ComponentItemData<T>> {
//   const get = useStateXGetter();
//   const mgr = (getManager(type) as unknown) as ComponentManager<T>;
//   if (!mgr) {
//     throw Error(`Unknown type ${type}`);
//   }
//   const item = get(itemAtom, {
//     params: { pageId, itemId },
//   }) as BaseComponentItem;
//   if (!item) {
//     throw Error(`Missing ${type} with itemId ${itemId}`);
//   }

//   const data: Required<ComponentItemData<T>> = (Object.keys(
//     mgr.defaultValue,
//   ) as (keyof ComponentItemTypeRegistry[T])[]).reduce((obj, key) => {
//     obj[key] = item.props[key] ?? mgr.defaultValue[key];
//     return obj;
//   }, ({} as unknown) as Required<ComponentItemData<T>>);

//   return data;
// }

function useMetadata<T>(pageId: ItemID, itemId: string, defaultValue: T) {
  return useData(pageId, itemId, defaultValue, 'design');
}

function useRuntime<T>(pageId: ItemID, itemId: string, defaultValue: T) {
  const { props, set, setProps } = useData(
    pageId,
    itemId,
    defaultValue,
    'runtime',
  );
  return {
    runtimeProps: props,
    setRuntimeProp: set,
    setRuntimeProps: setProps,
  };
}

function itemWithDefaultValues(item: Item<any>): RequiredItem<never> {
  const mgr = getManager(item.type);

  const props = Object.keys(mgr.defaultValue).reduce((obj, key) => {
    // @ts-ignore item.props[key]
    obj[key] = item.props?.[key] ?? mgr.defaultValue[key];
    return obj;
  }, {});

  return {
    ...item,
    // @ts-ignore props is not never
    props,
  };
}

const makeItem = <T extends ItemType>(
  platform: Platform,
  page: Page,
  parentId: ItemID,
  itemType: T,
  collect: Record<ItemID, AnyItem>,
): ItemID | null => {
  const mgr = getManager(itemType);
  // @ts-ignore dynamic item
  const newItem: AnyItem = {
    itemId: genId(page),
    type: mgr.type,
    children: [],
    parent: parentId,
    props: {},
  };
  if (parentId) {
    const collectedPage: Page = { ...page, ...collect };
    // ignore default children that are not allowed
    // e.g. FlexPanel inside Card Body with a auto layout in the parent path
    const parent = collectedPage[parentId];
    if (
      !mgr.isAllowedInsideParent(collectedPage, parent, {
        appUid: 'x',
        pageId: 'x',
        itemId: parentId,
        mgr,
        platform,
      })
    ) {
      return null;
    }
  }

  collect[newItem.itemId] = newItem;
  if (mgr.defaultChildren.length) {
    newItem.children = mgr.defaultChildren
      .map((child: ItemType) =>
        makeItem(platform, page, newItem.itemId, child, collect),
      )
      .filter((id) => id !== null) as string[];
  }
  return newItem.itemId;
};

const createItem = <T extends ItemType>({
  platform,
  page,
  parentId,
  itemType,
  itemId,
  appUid,
  pageId,
}: {
  platform: Platform;
  page: Page;
  parentId: string;
  itemType: T;
  itemId?: string;
  appUid: string;
  pageId: string;
}): { page: Page; newItemId: string; newItem: Item<T> } => {
  const mgr = getManager(itemType);
  // @ts-ignore dynamic item
  const newItem: AnyItem = {
    itemId: itemId || genId(page),
    type: mgr.type,
    children: [],
    parent: parentId,
    props: {},
  };
  // parentId will be empty when called from Component.tsx
  if (parentId) {
    // ignore default children that are not allowed
    // e.g. FlexPanel inside Card Body with a auto layout in the parent path
    const parent = page[parentId];
    if (
      mgr.isAllowedInsideParent(page, parent, {
        appUid,
        pageId,
        itemId: parentId,
        mgr,
        platform,
      }) !== true
    ) {
      return { page, newItemId: newItem.itemId, newItem: newItem as Item<T> };
    }

    let { children } = parent;
    children = [...children, newItem.itemId];
    page[parentId] = { ...parent, children };
  }
  page[newItem.itemId] = newItem;
  if (mgr.defaultChildren.length) {
    mgr.defaultChildren.forEach((child: ItemType) => {
      page = createItem({
        platform,
        page,
        parentId: newItem.itemId,
        itemType: child,
        appUid,
        pageId,
      }).page;
    });
  }
  return { page, newItemId: newItem.itemId, newItem: newItem as Item<T> };
};

export {
  registerComponent,
  createItem,
  makeItem,
  itemWithDefaultValues,
  useChildren,
  useMetadata,
  // useMetadataValueWithNoSubscription,
  useRuntime,
  useVisibleChildItems,
  wrapComponent,
};
